// API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://your-api-gateway-url.amazonaws.com/Prod';

// Helper function to build query parameters
const buildQueryParams = (params) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value);
    }
  });
  return searchParams.toString();
};

// Generic fetch function with error handling
const apiRequest = async (endpoint, options = {}) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
  
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      signal: controller.signal,
      ...options,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      // Handle specific HTTP status codes
      if (response.status === 400) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.error && errorData.error.includes('Invalid token')) {
          throw new Error('Invalid token: ' + errorData.error);
        }
        throw new Error(`Bad request: ${errorData.error || 'Invalid request parameters'}`);
      } else if (response.status === 404) {
        throw new Error('Resource not found');
      } else if (response.status === 500) {
        throw new Error('Server error. Please try again later.');
      } else if (response.status >= 500) {
        throw new Error('Server error. Please try again later.');
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    }

    const data = await response.json();
    return data;
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please check your connection and try again.');
    }
    
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    
    console.error('API request failed:', error);
    throw error;
  }
};

// Filter type to endpoint and parameter mapping
const FILTER_CONFIGS = {
  airport: {
    endpoint: '/photos/by-airport',
    apiParam: 'iata'
  },
  operator: {
    endpoint: '/photos/by-operator',
    apiParam: 'operator'
  },
  aircraftType: {
    endpoint: '/photos/by-aircraft-type',
    apiParam: 'aircraftType'
  },
  registration: {
    endpoint: '/photos/by-registration',
    apiParam: 'registration'
  },
  category: {
    endpoint: '/photos/by-category',
    apiParam: 'photoCategory'
  }
};

// Helper function to get endpoint for filter
const getEndpointForFilter = (filter) => {
  if (!filter || !filter.type || !filter.value) {
    return '/photos/all';
  }
  
  const config = FILTER_CONFIGS[filter.type];
  return config ? config.endpoint : '/photos/all';
};

// Helper function to get API parameter name for filter
const getApiParamForFilter = (filter) => {
  if (!filter || !filter.type) {
    return null;
  }
  
  const config = FILTER_CONFIGS[filter.type];
  return config ? config.apiParam : null;
};

// Fetch photos with optional single filter and pagination
export const fetchPhotos = async (filter = null, paginationParams = {}) => {
  // Determine endpoint based on filter
  const endpoint = getEndpointForFilter(filter);
  let queryParams = {};

  // Add filter parameter if filter is provided
  if (filter && filter.type && filter.value) {
    const apiParam = getApiParamForFilter(filter);
    if (apiParam) {
      queryParams[apiParam] = filter.value;
    }
  }

  // Add pagination parameters
  const { limit = 9, nextToken } = paginationParams;
  queryParams.limit = limit;
  
  if (nextToken) {
    queryParams.nextToken = nextToken;
  }

  // Build query string and make request
  const queryString = buildQueryParams(queryParams);
  const fullEndpoint = queryString ? `${endpoint}?${queryString}` : endpoint;

  return apiRequest(fullEndpoint);
};

// Fetch a single photo by ID
export const fetchPhotoById = async (photoId) => {
  return apiRequest(`/photos/${photoId}`);
};



// Helper function to get available filter types
export const getAvailableFilterTypes = () => {
  return Object.keys(FILTER_CONFIGS);
};

// Helper function to get filter configuration
export const getFilterConfig = (filterType) => {
  return FILTER_CONFIGS[filterType] || null;
};

// Export API base URL and filter configurations for potential use in components
export { API_BASE_URL, FILTER_CONFIGS };